import React, { useRef, useMemo } from 'react'
import styled from 'styled-components'
import { trackLinkClick } from 'api/analytics'
import { FacebookIcon } from 'icons/social/Facebook'
import { InstagramIcon } from 'icons/social/Instagram'
import { LinkedinIcon } from 'icons/social/Linkedin'
import { PinterestIcon } from 'icons/social/Pinterest'
import { TiktokIcon } from 'icons/social/Tiktok'
import { TwitterIcon } from 'icons/social/Twitter'
import { YoutubeIcon } from 'icons/social/Youtube'
import { Anchor } from 'lib/Link'

export type LinkType = 'instagram' | 'twitter' | 'linkedinUrl' | 'facebookUrl' | 'tiktokUrl' | 'youtubeUrl' | 'pintrestUrl'

type LinkDetails = {
  icon?: React.ReactNode
  label?: string
  url: string
}

const StyledAnchor = styled(Anchor)`
  margin: 0 8px;
  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    text-decoration: none;
  }
`

interface Props {
  brandTitle: string
  brandId: number | string
  value: string
  type: LinkType
}
export const SocialMediaLink = ({ brandTitle, brandId, type, value }: Props) => {
  const linkRef = useRef<HTMLAnchorElement>(null)

  const details = useMemo<LinkDetails>(() => {
    switch (type) {
      case 'instagram':
        return {
          icon: <InstagramIcon fill="black" />,
          url: value.match(/^http/) ? value : `https://instagram.com/${value}`
        }

      case 'twitter':
        return {
          icon: <TwitterIcon fill="black" />,
          url: value.match(/^http/) ? value : `https://twitter.com/${value}`
        }

      case 'linkedinUrl':
        return {
          icon: <LinkedinIcon fill="black" />,
          url: value
        }

      case 'facebookUrl':
        return {
          icon: <FacebookIcon fill="black" />,
          url: value
        }

      case 'tiktokUrl':
        return {
          icon: <TiktokIcon fill="black" />,
          url: value
        }

      case 'youtubeUrl':
        return {
          icon: <YoutubeIcon fill="black" />,
          url: value
        }

      case 'pintrestUrl':
        return {
          icon: <PinterestIcon fill="black" />,
          url: value
        }

      default:
        return {
          label: value,
          url: value
        }
    }
  }, [type, value])

  if (!details) {
    return null
  }

  return (
    <StyledAnchor
      underlineOnNormalState={false}
      ref={linkRef}
      href={details.url}
      target="_blank"
      rel="noopener"
      aria-label={`${brandTitle} ${type} link`}
      onClick={() => {
        trackLinkClick(`Social ${type} Link Clicked`, brandTitle, { brandId }, linkRef.current)
      }}>
      {details.label ?? details.icon}
    </StyledAnchor>
  )
}
